import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchUiPreferences, updateUiPreferences } from '../services/api';

/**
 * Hides the last 4 digits of a mobile number, leaving the country code and any
 * spacing intact: '+6598391733' → '+659839••••'. Digits are masked from the
 * right so formatted input ('+65 9839 1733') keeps its shape.
 */
export function maskMobileNumber(mobile?: string | null): string {
  const value = (mobile || '').trim();
  if (!value) return '';
  let remaining = 4;
  const chars = value.split('');
  for (let i = chars.length - 1; i >= 0 && remaining > 0; i--) {
    if (/\d/.test(chars[i])) {
      chars[i] = '•';
      remaining--;
    }
  }
  return chars.join('');
}

interface MobileMaskValue {
  /** Whether mobile numbers are currently masked. Starts masked. */
  masked: boolean;
  /** True until the stored preference has loaded. */
  loading: boolean;
  setMasked: (next: boolean) => void;
  /** Mobile as it should be displayed under the current setting. */
  formatMobile: (mobile?: string | null) => string;
}

const MobileMaskContext = createContext<MobileMaskValue>({
  masked: true,
  loading: false,
  setMasked: () => {},
  formatMobile: (mobile) => maskMobileNumber(mobile),
});

/**
 * Holds the agent's mobile-masking preference, loaded from (and saved back to)
 * Firestore via /user/ui-preferences so the choice survives reloads and devices.
 * Masked is the default: an agent who has never toggled it — or whose preference
 * can't be read — sees masked numbers.
 */
export function MobileMaskProvider({ children }: { children: React.ReactNode }) {
  const [masked, setMaskedState] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchUiPreferences()
      .then((prefs) => { if (!cancelled) setMaskedState(prefs.maskMobile); })
      .catch(() => { /* keep the masked default */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Optimistic: flip immediately, revert if the save fails so the UI never
  // claims a preference that wasn't stored.
  const setMasked = useCallback((next: boolean) => {
    setMaskedState(next);
    updateUiPreferences({ maskMobile: next }).catch((err) => {
      console.error('Failed to save mobile-mask preference:', err);
      setMaskedState(!next);
    });
  }, []);

  const value = useMemo<MobileMaskValue>(() => ({
    masked,
    loading,
    setMasked,
    formatMobile: (mobile) => (masked ? maskMobileNumber(mobile) : (mobile || '').trim()),
  }), [masked, loading, setMasked]);

  return <MobileMaskContext.Provider value={value}>{children}</MobileMaskContext.Provider>;
}

export function useMobileMask(): MobileMaskValue {
  return useContext(MobileMaskContext);
}
